/**
 * Closed vocabularies of the domain. The database enforces the same lists with
 * check constraints (packages/db), and API schemas are built from them.
 */
export const ORG_KINDS = ['client', 'platform'] as const
export const ORG_STATUSES = ['active', 'suspended', 'offboarded'] as const
export const UNIT_KINDS = ['department', 'team', 'area', 'site'] as const
export const MEMBER_ROLES = ['employee', 'hr_admin'] as const
export const MEMBER_STATUSES = ['invited', 'active', 'deactivated'] as const
/**
 * A person's data that HR edits besides their unit and role, as `person.updated` names the
 * ones it changed (never their values). The unit and the role have entries of their own.
 */
export const PERSON_DATA_FIELDS = [
  'first_name',
  'last_name',
  'job_title',
  'start_date',
  'locale',
] as const
/** Roles a request can act with. 'operator' is Eklevo staff through the audited path. */
export const CONTEXT_ROLES = ['employee', 'hr_admin', 'operator'] as const
export const ACTOR_KINDS = ['user', 'operator', 'system'] as const

export const COURSE_ORIGINS = ['eklevo', 'organization'] as const
export const COURSE_STATUSES = ['draft', 'published', 'archived'] as const
export const VERSION_STATES = ['draft', 'published'] as const
export const VIDEO_SOURCES = ['none', 'file', 'external'] as const
export const MATERIAL_KINDS = ['pdf', 'link'] as const
export const ASSESSMENT_KINDS = ['intermediate', 'final'] as const
export const PRACTICE_KINDS = ['identification', 'noa', 'case', 'other'] as const
/** Where a duplicate of a course stands, as it runs in the background (ADR 0009 §8). */
export const DUPLICATION_STATUSES = ['running', 'done', 'failed'] as const
/**
 * Why a duplicate failed, a code and never its content: its files could not be copied, it could not
 * be written (also because the session ended meanwhile), or its task stopped halfway (a restart).
 */
export const DUPLICATION_ERRORS = ['copy-failed', 'write-failed', 'interrupted'] as const

export const PROGRAM_STATUSES = ['not_launched', 'active', 'stopped', 'completed'] as const
export const ASSIGNMENT_STATUSES = ['not_started', 'in_progress', 'completed'] as const
export const ASSIGNMENT_ORIGINS = ['launch', 'individual', 'operator'] as const
/** The periods of «Informes» (PRD 03 §2.9, Figma 6:3338): the last month, quarter or year. */
export const REPORT_PERIODS = ['month', 'quarter', 'year'] as const
/**
 * Why HR grants extra attempts at an assessment (ADR 0005 §6.6): a reason from a list, so that
 * no free text is needed (an optional note of one line may go with it). The operator's grants
 * keep their own text instead.
 */
export const GRANT_REASON_CODES = ['technical_issue', 'absence', 'other'] as const

/**
 * The time zones an organization may have (ADR 0009 §7): Spain's peninsula, the default, and the
 * Canary Islands. Its dates, due dates, reminders and reports are days of its calendar there
 * (PRD 00 §4.7). The database's function that creates a company from the back office checks the
 * same list (migration 0025).
 */
export const TIME_ZONES = ['Europe/Madrid', 'Atlantic/Canary'] as const

export const FILE_PURPOSES = ['avatar', 'logo', 'material', 'video', 'captions', 'diploma'] as const
export const FILE_STATES = ['quarantine', 'clean', 'rejected'] as const

/**
 * Templates of the email queue. A sign-in link is queued only by the authentication module;
 * the others only by staff (HR or the operator). Every email but a sign-in link goes to the
 * address the database gives for it, never to the one in the row (ADR 0005 §3). The worker
 * builds an assignment, a reminder and a notice of the HR role from the database; an invitation
 * still takes the person's first name, the organization, the privacy notice's address and the
 * language from its row.
 */
export const EMAIL_TEMPLATES = [
  'login-request',
  'invitation',
  'assignment',
  'reminder',
  'hr-role-changed',
] as const

export type OrgKind = (typeof ORG_KINDS)[number]
export type OrgStatus = (typeof ORG_STATUSES)[number]
export type TimeZone = (typeof TIME_ZONES)[number]
export type UnitKind = (typeof UNIT_KINDS)[number]
export type MemberRole = (typeof MEMBER_ROLES)[number]
export type MemberStatus = (typeof MEMBER_STATUSES)[number]
export type PersonDataField = (typeof PERSON_DATA_FIELDS)[number]
export type ContextRole = (typeof CONTEXT_ROLES)[number]
export type ActorKind = (typeof ACTOR_KINDS)[number]
export type CourseOrigin = (typeof COURSE_ORIGINS)[number]
export type CourseStatus = (typeof COURSE_STATUSES)[number]
export type VersionState = (typeof VERSION_STATES)[number]
export type VideoSource = (typeof VIDEO_SOURCES)[number]
export type MaterialKind = (typeof MATERIAL_KINDS)[number]
export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number]
export type PracticeKind = (typeof PRACTICE_KINDS)[number]
export type DuplicationStatus = (typeof DUPLICATION_STATUSES)[number]
export type DuplicationError = (typeof DUPLICATION_ERRORS)[number]
export type ProgramStatus = (typeof PROGRAM_STATUSES)[number]
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number]
export type AssignmentOrigin = (typeof ASSIGNMENT_ORIGINS)[number]
export type ReportPeriod = (typeof REPORT_PERIODS)[number]
export type GrantReasonCode = (typeof GRANT_REASON_CODES)[number]
export type FilePurpose = (typeof FILE_PURPOSES)[number]
export type FileState = (typeof FILE_STATES)[number]
export type EmailTemplate = (typeof EMAIL_TEMPLATES)[number]

/** Fixed id of the Eklevo platform organization, owner of the global catalog. */
export const PLATFORM_ORG_ID = '01900000-0000-7000-8000-000000000001'
